import { Component, Input, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DropdownOption {
  value: string;
  label: string;
  description?: string;
}

@Component({
  selector: 'hc-input-dropdown',
  templateUrl: './input-dropdown.component.html',
  styleUrls: ['./input-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDropdownComponent),
      multi: true
    }
  ]
})
export class InputDropdownComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select...';
  @Input() options: DropdownOption[] = [];
  @Input() inputId = '';
  @Input() hasError = false;
  @Input() disabled = false;

  value = '';
  isOpen = false;
  focused = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef, private elRef: ElementRef) {}

  writeValue(value: string | number | null): void {
    this.value = value != null ? String(value) : '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  toggle(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.focused = this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
    }
  }

  select(option: DropdownOption): void {
    this.value = option.value;
    this.onChange(this.value);
    this.isOpen = false;
    this.focused = false;
    this.onTouched();
  }

  get selectedLabel(): string {
    // Use loose comparison to handle number/string mismatches (e.g., form value 3 vs option "3")
     
    const found = this.options.find(o => o.value == this.value);
    return found ? found.label : '';
  }

  isSelected(option: DropdownOption): boolean {
     
    return option.value == this.value;
  }

  get containerClass(): string {
    const classes = ['hc-input', 'hc-input--dropdown'];
    if (this.focused) classes.push('hc-input--focused');
    if (this.hasError) classes.push('hc-input--error');
    if (this.disabled) classes.push('hc-input--disabled');
    if (this.value) classes.push('hc-input--filled');
    return classes.join(' ');
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.focused = false;
      this.cdr.markForCheck();
    }
  }
}
